import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "1. Introduction",
      content: (
        <>
          <p>
            HireCura is a healthcare recruitment platform operated by
            HireCura Technologies Private Limited ("HireCura", "we", "us",
            or "our"). We connect healthcare professionals with hospitals,
            clinics, laboratories, healthcare institutions, and other
            employers across India.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose,
            store, and protect personal information when you visit our
            website, create an account, apply for jobs, post vacancies,
            or otherwise use our services.
          </p>
        </>
      ),
    },
    {
      title: "2. Information We Collect",
      content: (
        <>
          <p>Depending on how you use HireCura, we may collect:</p>
          <ul>
            <li>
              <strong>Account information:</strong> Name, email address,
              phone number, login credentials, and account type.
            </li>
            <li>
              <strong>Candidate information:</strong> Resume, education,
              qualifications, professional experience, skills, employment
              preferences, and other details you choose to provide.
            </li>
            <li>
              <strong>Employer information:</strong> Institution or
              organization name, business contact details, recruiter
              information, job postings, and related company information.
            </li>
            <li>
              <strong>Application information:</strong> Jobs applied for,
              application details, and relevant communications through
              the platform.
            </li>
            <li>
              <strong>Technical information:</strong> IP address, browser
              type, device information, access logs, and usage data where
              collected through our website or technical services.
            </li>
            <li>
              <strong>Communications:</strong> Messages, support requests,
              feedback, and information you provide when contacting us.
            </li>
          </ul>
          <p>
            Please do not submit unnecessary sensitive personal information
            in resumes, messages, or job applications.
          </p>
        </>
      ),
    },
    {
      title: "3. How We Use Your Information",
      content: (
        <>
          <p>We may use personal information to:</p>
          <ul>
            <li>Create and manage candidate and employer accounts.</li>
            <li>Display job vacancies and facilitate applications.</li>
            <li>
              Allow employers to review candidate information submitted
              or made available to them through the platform.
            </li>
            <li>Help candidates discover relevant job opportunities.</li>
            <li>Verify accounts and help prevent fraud or misuse.</li>
            <li>Respond to support requests and communicate service updates.</li>
            <li>Maintain, secure, troubleshoot, and improve HireCura.</li>
            <li>
              Meet applicable legal obligations and enforce our platform
              policies.
            </li>
          </ul>
          <p>
            We will process personal information for appropriate purposes
            and in accordance with applicable law and the choices or
            permissions available to you.
          </p>
        </>
      ),
    },
    {
      title: "4. How Information Is Shared",
      content: (
        <>
          <p>We may share information in the following circumstances:</p>
          <ul>
            <li>
              <strong>With employers:</strong> Information you submit with
              an application or choose to make available to recruiters.
            </li>
            <li>
              <strong>With candidates:</strong> Employer and job information
              necessary to display vacancies and facilitate applications.
            </li>
            <li>
              <strong>With service providers:</strong> Trusted providers
              supporting hosting, security, communications, analytics,
              and platform operations, where applicable.
            </li>
            <li>
              <strong>For legal reasons:</strong> Where disclosure is
              required or permitted by applicable law or a valid legal
              process.
            </li>
            <li>
              <strong>Business changes:</strong> In connection with a
              merger, restructuring, acquisition, or transfer of business,
              subject to applicable legal requirements.
            </li>
          </ul>
          <p>
            We do not promise that an employer who receives your application
            will keep it confidential unless an applicable agreement or
            legal obligation requires it. Employers may process candidate
            information under their own privacy practices.
          </p>
        </>
      ),
    },
    {
      title: "5. Public Profiles and Job Applications",
      content: (
        <>
          <p>
            Information included in a public profile, job posting, or other
            publicly accessible area may be visible to people beyond your
            intended audience. Review the information you choose to publish.
          </p>
          <p>
            When you apply for a job, information included in your
            application may be shared with the relevant employer. Removing
            information from HireCura may not remove copies already
            downloaded, retained, or processed by that employer.
          </p>
        </>
      ),
    },
    {
      title: "6. Cookies and Similar Technologies",
      content: (
        <>
          <p>
            HireCura may use cookies or similar technologies to support
            essential website functions, maintain sessions, improve
            security, remember preferences, and understand website usage.
          </p>
          <p>
            Where required, we will seek consent for non-essential cookies
            or similar technologies. You may also manage cookies through
            your browser settings. Disabling certain cookies may affect
            some website features.
          </p>
        </>
      ),
    },
    {
      title: "7. Data Security and Retention",
      content: (
        <>
          <p>
            We use reasonable technical, organizational, and administrative
            safeguards designed to protect personal information against
            unauthorized access, loss, misuse, alteration, or disclosure.
            No internet-based service can guarantee absolute security.
          </p>
          <p>
            We retain information for as long as reasonably necessary to
            provide the services, maintain accounts, facilitate recruitment,
            resolve disputes, meet legal obligations, and enforce our
            agreements. Retention periods depend on the type of information
            and the relevant purpose.
          </p>
        </>
      ),
    },
    {
      title: "8. Your Choices and Privacy Rights",
      content: (
        <>
          <p>
            Subject to applicable law, you may be able to request access
            to, correction of, or deletion of your personal information,
            withdraw consent where processing is based on consent, or
            exercise other applicable privacy rights.
          </p>
          <p>
            You may also update certain account details through your
            account settings, where available. Some information may need
            to be retained where the law permits or requires it.
          </p>
          <p>
            To make a privacy request, contact us using the details below.
            We may need to verify your identity before responding.
          </p>
        </>
      ),
    },
    {
      title: "9. Third-Party Services and Links",
      content: (
        <p>
          Our website may contain links to third-party websites or use
          third-party services. Those services may have their own terms
          and privacy policies. HireCura is not responsible for the privacy
          practices of third parties, except where applicable law provides
          otherwise.
        </p>
      ),
    },
    {
      title: "10. Children's Privacy",
      content: (
        <p>
          HireCura is intended for people who are legally eligible to use
          the relevant recruitment services. We do not knowingly collect
          children's personal data in violation of applicable law. Where
          required, we will obtain appropriate consent or take other
          legally required steps.
        </p>
      ),
    },
    {
      title: "11. Changes to This Policy",
      content: (
        <p>
          We may update this Privacy Policy as our services, practices,
          or legal obligations change. We will publish the updated version
          on this page and revise the "Last updated" date. Where required,
          we will provide additional notice or obtain consent.
        </p>
      ),
    },
    {
      title: "12. Contact Us",
      content: (
        <>
          <p>
            For privacy questions, requests, or complaints, contact:
          </p>
          <p>
            <strong>HireCura Technologies Private Limited</strong>
            <br />
            Hyderabad, Telangana, India
            <br />
            Email: support@hirecura.com
          </p>
        </>
      ),
    },
  ];

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
      <article className="mx-auto max-w-4xl overflow-hidden rounded-2xl
                          border border-gray-100 bg-white shadow-sm">
        <header className="border-b border-gray-100 px-6 py-10 sm:px-10">
          <Link to="/" className="text-2xl font-bold tracking-tight">
            <span className="text-[#0F172A]">Hire</span>
            <span className="text-[#0D9488]">Cura</span>
          </Link>

          <p className="mt-8 text-sm font-semibold uppercase tracking-widest
                        text-[#0D9488]">
            Legal
          </p>

          <h1 className="mt-2 text-3xl font-bold text-[#0F172A]
                         sm:text-4xl">
            Privacy Policy
          </h1>

          <p className="mt-4 text-sm text-gray-500">
            Last updated: 15 October 2026
          </p>

          <p className="mt-5 max-w-3xl leading-7 text-gray-600">
            Your privacy matters to us. This policy explains how HireCura
            handles personal information when you use our healthcare
            recruitment platform.
          </p>
        </header>

        <div className="space-y-8 px-6 py-8 sm:px-10">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold text-[#0F172A]">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3 leading-7 text-gray-600
                              [&_ul]:list-disc [&_ul]:space-y-2
                              [&_ul]:pl-6">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        <div className="border-t border-gray-200 px-6 py-6 sm:px-10">
          <Link
            to="/terms-and-conditions"
            className="font-medium text-[#0D9488] hover:underline"
          >
            Read Terms &amp; Conditions →
          </Link>
        </div>
      </article>
    </main>
  );
};

export default PrivacyPolicy;